<template>
  <div id="cover-page">
    <img
      :src="imagePair.header"
      alt="spyglass logo"
      class="header-img"
      data-cy="header-image"
    />
    <div class="red-band align-self-end"></div>
    <div class="flex" data-cy="sizing-report-container">
      <span class="sizing-report">
        PERFORMANCE EVALUATION
        <span>REPORT</span>
      </span>
    </div>

    <div
      v-for="field in fields"
      :key="field.label"
      class="field-row"
      :data-cy="`field-${field.label.toLowerCase().replace(/\s/g, '-')}`"
    >
      <span class="icon-span mr-5"><icon :icon="field.icon" /></span>
      <div>
        <p class="field-name">{{ field.label }}</p>
        <p class="field-value">{{ field.value }}</p>
      </div>
    </div>

    <p class="mt-auto prepared-by" data-cy="prepared-by">
      Prepared by {{ user.name }}
    </p>
    <div :class="`red-band ${imagePair.footerClass}`"></div>

    <img
      :src="imagePair.footer"
      :class="`report-cover-footer ${imagePair.footerClass}`"
      alt="summit footer"
      data-cy="summit-footer-image"
    />
  </div>
</template>

<script setup lang="ts">
import useUserInfo from "@/composables/auth/useUserInfo";
import type {
  FormItems,
  Scenario,
  Sizing
} from "@/composables/forms/formTypes";
import useReportCoverImages from "@/composables/ui/useReportCoverImages";
import { computed } from "vue";

const props = defineProps<{
  sizing?: Sizing | null;
  scenario?: Scenario | null;
  perfEval?: FormItems["performanceEvaluation"] | null;
  dateGenerated: string;
}>();
const user = useUserInfo();
const imagePair = useReportCoverImages(false);
interface Field {
  icon: [string, string];
  label: string;
  value: string;
}
const fields = computed<Field[]>(() => [
  {
    icon: ["fas", "user"],
    label: "Customer",
    value: props.sizing?.customer?.customer_name || "TBD"
  },
  {
    icon: ["fas", "droplet"],
    label: "Well",
    value: props.sizing?.well?.well_name || "TBD"
  },
  {
    icon: ["fas", "pen-ruler"],
    label: "Sizing",
    value: props.sizing?.title || "No Sizing Title"
  },
  {
    icon: ["fas", "info"],
    label: "Scenario",
    value: props.scenario?.title || "No Scenario Title"
  },
  {
    icon: ["fas", "heartbeat"],
    label: "Performance Evaluation",
    value: props.perfEval?.data.value.title || "No Perf Eval Title"
  },
  {
    icon: ["fas", "calendar"],
    label: "Date Generated",
    value: props.dateGenerated
  }
]);
</script>

<style lang="scss" scoped>
#cover-page {
  width: 100%;
  min-height: 11in;
  display: flex;
  flex-direction: column;
  position: relative;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  .header-img {
    width: 100%;
    object-fit: contain;
  }

  .sizing-report {
    font-size: 30px;
    letter-spacing: 4px;
    padding: 20px 36px;
    margin: -44px 0 0 -1px;
    background-color: white;
    span {
      font-weight: 200;
    }
  }

  .field-row {
    display: flex;
    align-items: center;
    margin: 25px 0 0 30px;

    .icon-span {
      background: linear-gradient(0deg, #ed213a -62.5%, #93291e 100%);
      border-radius: 50%;
      width: 34px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      svg {
        color: white;
      }
    }

    .field-name {
      font-size: 14px;
      font-weight: 300;
      color: var(--gray-800);
      margin: 0;
    }
    .field-value {
      font-size: 16px;
      margin: 0;
    }
  }

  .prepared-by {
    font-size: 14px;
    padding: 14px 35px;
    color: var(--gray-800);
  }

  .red-band {
    height: 9px;
    background: linear-gradient(90deg, #ed213a 5.27%, #93291e 100%);
    width: 70%;
    margin-top: -1px;
    &.summit-footer {
      width: 100%;
    }
  }

  .report-cover-footer {
    position: absolute;
    right: 0;
    bottom: 0px;
    width: 300px;
    &.summit-footer {
      right: 0.5px;
      bottom: 10px;
    }
  }
}

@media print {
  @page {
    height: 100vh;
  }

  #cover-page {
    page-break-after: always;
    page-break-inside: avoid;
    height: 100vh;
  }
}
</style>
